<template>
  <Loading v-if="!didLoadAnnouncementTypes" />
  <div v-else>
    <!-- Dialogs -->
    <ConfirmDialog 
      :model="showDeleteDialog" 
      :onConfirm="attemptDeleteAnnouncementType"
      :onCancel="() => announcementTypeToDelete = undefined">
      {{`Přejete si smazat typ oznámení s názvem "${announcementTypeToDelete.name}"?`}}
      Tato operace je nevratná.
    </ConfirmDialog>
    <Alert 
      :model="errorOnDelete" 
      :onConfirm="() => errorOnDelete = false">
      Zvolený typ oznámení nelze smazat, je využíván!
    </Alert>
    <!-- Module header -->
    <BackButton :target="{ name: 'unitDetail' }">Informační panely</BackButton>
    <ModuleHeader v-if="getAnnouncementTypes.length > 0">
      <RefreshButton @click="refreshAnnouncementTypes" title="Aktualizovat data">
        <h2>Typy oznámení{{ didLoadAdministrators ? ` - ${selectedUnit.localizedAttributes.cz.name}` : '' }}</h2>
      </RefreshButton>
      <PrimaryButton id="add-announcement-type" :to="{ name: 'announcementTypeDetail', params: { announcementTypeId: 'new' } }">+ Nový</PrimaryButton>
    </ModuleHeader>
    <ModuleHeader v-else>
      <h2>Typy oznámení</h2>
    </ModuleHeader>
    <v-container v-if="getAnnouncementTypes.length > 0">
        <!-- Announcement types data table -->
      <div class="announcement-types-table">
        <v-data-table
          :headers="headers"
          :items="getAnnouncementTypes"
          class="d-none d-sm-block"
          items-per-page="25"
        >
          <template v-slot:item.iconName="{ item }">
            <v-icon :style="{ '--base-color': item.color }" class="announcement-type-icon" :title="item.iconName">{{ item.iconName }}</v-icon>
          </template>
          <template v-slot:item.name="{ item }">
            <TitledText id="type-name" :title="item.name" :text="ellipsis(item.name, 55)" />
          </template>
          <template v-slot:item.displaysNames="{ item }">
            <div v-if="(item.displaysNames.length || 0) > 0" style="padding: 0.2em 0 0.2em 0">
              <v-chip v-for="(name, index) in item.displaysNames" :key="`display-${index}`" x-small class="display-chip">
                {{ name }}
              </v-chip>
            </div>
            <span v-else class="no-displays-text">typ oznámení není zobrazován</span>
          </template>
          <template v-slot:item.serviceName="{ item }">
            <TitledText :title="item.serviceName" :text="item.serviceName ? getServiceName(item.serviceName) : '-'" />
          </template>
          <template v-slot:item.actions="{ item }">
            <EditIcon id="edit-announcement-type" title="Upravit typ oznámení" @click="goToDetail(item.id)" />
            <DeleteIcon id="remove-announcement-type" title="Smazat typ oznámení" @click="() => announcementTypeToDelete = item" />
          </template>
        </v-data-table>
        <!-- Announcement types table for smaller screens -->
        <v-data-iterator
          :items="getAnnouncementTypes"
          :sort-by="[$query.sort]"
          :sort-desc="$query.desc"
          @update:sort-by="value => $query.sort = value"
          @update:sort-desc="value => $query.desc = value"
          :hide-default-footer="true"
          :disable-pagination="true"
          :hide-default-header="false"
          class="d-block d-sm-none pt-1"
        >
          <template v-slot:no-data>Žádná dostupná data</template>
          <template v-slot:default="props">
            <v-row>
              <v-col v-for="item in props.items" :key="item.raw.id" cols="12">
                <MobileDataTableRow
                  @click="goToDetail(item.raw.id)"
                  @action="() => announcementTypeToDelete = item.raw"
                  :extra="getDisplayNamesForMobile(item.raw.displaysNames)"
                >
                  <template v-slot:number>{{ item.raw.id }}</template>
                  <template v-slot:title>{{ ellipsis(item.raw.name, 24) }}</template>
                  <template v-slot:label_header1>SYMBOL</template>
                  <template v-slot:label_header2>GENERUJÍCÍ SLUŽBA</template>
                  <template v-slot:label_content1>
                    <v-icon :style="{ '--base-color': item.raw.color }" class="announcement-type-icon" :title="item.raw.iconName">{{ item.raw.iconName }}</v-icon>
                  </template>
                  <template v-slot:label_content2>{{ item.raw.serviceName ? getServiceName(item.raw.serviceName) : '-' }}</template>
                  <template v-slot:action_button>delete_outline</template>
                </MobileDataTableRow>
              </v-col>
            </v-row>
          </template>
        </v-data-iterator>
      </div>
    </v-container>
    <v-container class="text-center" v-else>
      <img :src="PlaceholderImage" id="no-types-placeholder" />
      <v-row>
        <v-col>
          <p class="mb-10">Dosud nebyly definovány žádné typy oznámení...</p>
        </v-col>
      </v-row>
      <PrimaryButton id="add-announcement-type" :to="{ name: 'announcementTypeDetail', params: { announcementTypeId: 'new' } }">+ Nový typ oznámení</PrimaryButton>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { displaysServices } from '@/config';
import { announcementTypesHeaders } from './announcement-types-helpers';
import Alert from '@/components/dialogs/Alert';
import BackButton from "@/components/layout/BackButton";
import ConfirmDialog from '@/components/dialogs/ConfirmDialog';
import DeleteIcon from "@/components/buttons/DeleteIcon";
import EditIcon from "@/components/buttons/EditIcon";
import Loading from "@/components/Loading";
import MobileDataTableRow from '@/components/layout/MobileDataTableRow.vue';
import ModuleHeader from "@/components/layout/ModuleHeader";
import PrimaryButton from "@/components/buttons/PrimaryButton";
import RefreshButton from "@/components/buttons/RefreshButton";
import TitledText from '@/components/text/TitledText';
import PlaceholderImage from '@/assets/displays/no_announcement_types.svg?url';
import { ellipsis } from '@/filters/ellipsis';

const store = useStore();
const route = useRoute();
const router = useRouter();

const headers = announcementTypesHeaders;
const announcementTypeToDelete = ref(undefined);
const errorOnDelete = ref(false);

const didLoadAdministrators = computed(() => store.getters['displaysAdmins/didLoadAdministrators']);
const getAnnouncementTypes = computed(() => store.getters['announcementTypes/getAnnouncementTypes']);
const didLoadAnnouncementTypes = computed(() => store.getters['announcementTypes/didLoadAnnouncementTypes']);
const getAdministrators = computed(() => store.getters['displaysAdmins/getAdministrators']);
const username = computed(() => store.getters['user/username']);

const showDeleteDialog = computed(() => announcementTypeToDelete.value !== undefined);
const selectedUnit = computed(() => getAdministrators.value.find(user => user.xlogin === username.value)?.faculties?.find(faculty => faculty.id === Number(route.params.unitId)));

const fetchData = async () => {
  await store.dispatch('displaysAdmins/fetchAdministrators');
  await store.dispatch('announcementTypes/fetchAnnouncementTypes', route.params.unitId);
};

const refreshAnnouncementTypes = () => {
  store.dispatch('announcementTypes/fetchAnnouncementTypes', route.params.unitId);
};

const attemptDeleteAnnouncementType = () => {
  store.dispatch('announcementTypes/deleteAnnouncementType', announcementTypeToDelete.value.id).catch(() => {
    errorOnDelete.value = true;
  });
  announcementTypeToDelete.value = undefined;
};

const goToDetail = (announcementTypeId) => {
  router.push({
    name: 'announcementTypeDetail',
    params: { announcementTypeId }
  });
};

const getServiceName = (id) => {
  const foundService = displaysServices.find(item => item.id === id);
  return foundService ? foundService.name : '-';
};

const getDisplayNamesForMobile = (displaysNames) => {
  return displaysNames?.map(name => ({ title: name }));
};

onMounted(fetchData);
</script>

<style scoped>
.announcement-types-table {
  margin-top: 1rem;
  width: 100%;
}

.announcement-type-icon {
  background-color: var(--base-color);
  font-size: 1.5em !important;
  padding: 0.3em;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: white !important;
}

.display-chip {
  margin: 0.2em;
}

.no-displays-text {
  padding: 0.2em;
}

.container {
  margin-left: 0;
  margin-right: 0;
  padding-left: 0;
  padding-right: 0;
  min-width: 100%;
}
</style>
